import React, { useCallback, useEffect, useState } from 'react';
import { Row, Col, Card, Button, Badge, Modal, Form } from 'react-bootstrap';
import API from '../services/api';
import { useAuth } from '../context/AuthContext';
import { EmergencyModal } from './EmergencyControls';
import { dateTime } from './adminShared';
import { formatDay } from '../utils/dates';

// Shown once, right after generating: the codes exist nowhere else in readable form.
const CodesModal = ({ codes, onClose }) => {
  const [copied, setCopied] = useState(false);
  const text = codes.map((c, i) => `${i + 1}. ${c}`).join('\n');
  return (
    <Modal show onHide={onClose} backdrop="static" centered>
      <Modal.Header closeButton><Modal.Title>Tus códigos de recuperación</Modal.Title></Modal.Header>
      <Modal.Body>
        <div className="alert alert-danger py-2 small">
          <strong>Guárdalos ahora, fuera de línea</strong> (impresos, en una caja fuerte). No se pueden volver a ver, y los anteriores ya no funcionan.
          Cada código sirve una sola vez.
        </div>
        <div className="p-3" style={{ fontFamily: 'monospace', fontSize: '1.1rem', letterSpacing: '0.08em', background: 'var(--mm-panel-2)', borderRadius: 8, columnCount: 2 }}>
          {codes.map((c, i) => <div key={c}>{i + 1}. {c}</div>)}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant={copied ? 'success' : 'outline-primary'} onClick={() => { navigator.clipboard?.writeText(text); setCopied(true); }}>{copied ? 'Copiados' : 'Copiar'}</Button>
        <Button variant="outline-primary" onClick={() => {
          const w = window.open('', '_blank', 'width=520,height=640');
          if (w) { w.document.write(`<pre style="font:18px monospace;line-height:2">Ventas — códigos de recuperación\n${formatDay(new Date())}\n\n${text}\n\nCada código sirve una sola vez.</pre>`); w.document.close(); w.print(); }
        }}>Imprimir</Button>
        <Button onClick={onClose}>Ya los guardé</Button>
      </Modal.Footer>
    </Modal>
  );
};

const AdminEmergencyPanel = () => {
  const { user } = useAuth();
  const [lock, setLock] = useState(null);
  const [status, setStatus] = useState(null); // recovery codes (owners)
  const [arming, setArming] = useState(false);
  const [askPassword, setAskPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [codes, setCodes] = useState(null);

  const load = useCallback(() => {
    API.get('/admin/lockdown').then(r => setLock(r.data)).catch(() => {});
    if (user?.isOwner) API.get('/account/recovery-codes').then(r => setStatus(r.data)).catch(() => {});
  }, [user?.isOwner]);
  useEffect(() => {
    load();
    window.addEventListener('ventas-lockdown-changed', load);
    return () => window.removeEventListener('ventas-lockdown-changed', load);
  }, [load]);

  const generate = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      const { data } = await API.post('/account/recovery-codes', { currentPassword: password });
      setCodes(data.codes); setAskPassword(false); setPassword(''); load();
    } catch (err) { setError(err.response?.data?.message || 'No se pudieron generar los códigos.'); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Row className="g-3 mb-3">
        <Col lg={6} className="mb-3">
          <Card className="shadow-sm p-3 h-100">
            <Card.Title style={{ fontSize: '1.05rem' }}><i className="bi bi-shield-exclamation me-2" style={{ color: 'var(--mm-red)' }} />Bloqueo de emergencia{' '}
              <Badge bg={lock?.active ? 'danger' : 'secondary'}>{lock?.active ? 'ACTIVO' : 'Apagado'}</Badge></Card.Title>
            <div className="small text-muted mb-3">
              Congela el sistema para todos menos los propietarios, cierra las demás sesiones y avisa por correo. Cualquier administrador puede activarlo;
              solo un propietario puede levantarlo. También está en el botón rojo del encabezado.
            </div>
            {lock?.active && <div className="small mb-2">Activo desde {dateTime(lock.since)} por <strong>{lock.by}</strong>{lock.reason ? ` — ${lock.reason}` : ''}. Se levanta desde la barra roja de arriba.</div>}
            <div><Button variant="danger" disabled={lock?.active} onClick={() => setArming(true)}>Activar bloqueo de emergencia</Button></div>
          </Card>
        </Col>

        <Col lg={6} className="mb-3">
          <Card className="shadow-sm p-3 h-100">
            <Card.Title style={{ fontSize: '1.05rem' }}><i className="bi bi-key me-2" style={{ color: 'var(--mm-cyan)' }} />Acceso de propietario y recuperación</Card.Title>
            {user?.isOwner ? (
              <>
                <div className="small text-muted mb-2">
                  Eres <strong>propietario</strong>: nadie más puede borrar, desactivar ni cambiarte la contraseña o el correo. Como respaldo, ten a mano tus códigos de recuperación
                  (sirven en la página <code>/recuperar</code> si pierdes la contraseña o alguien la cambia).
                </div>
                <div className="mb-2">
                  Códigos de recuperación: <strong>{status ? `${status.remaining} de ${status.total} disponibles` : '…'}</strong>
                  {status?.generatedAt && <span className="small text-muted"> · generados el {dateTime(status.generatedAt)}</span>}
                  {status && status.generatedAt === null && <div className="small text-warning">Aún no tienes códigos. Genéralos y guárdalos fuera de línea.</div>}
                  {status && status.generatedAt && status.remaining <= 2 && <div className="small text-warning">Te quedan pocos: genera un juego nuevo.</div>}
                </div>
                <div><Button variant="outline-primary" onClick={() => { setError(''); setAskPassword(true); }}>{status?.generatedAt ? 'Generar códigos nuevos' : 'Generar códigos de recuperación'}</Button></div>
              </>
            ) : (
              <div className="small text-muted">Solo los propietarios tienen códigos de recuperación y pueden administrar a otros administradores.</div>
            )}
            <hr style={{ borderColor: 'var(--mm-line)' }} />
            <div className="small text-muted">
              Si te quedas sin acceso y puedes entrar al servidor (SSH), la herramienta de recuperación funciona aunque la aplicación esté bloqueada o comprometida:
              <pre className="mt-2 mb-0 p-2 small" style={{ background: 'var(--mm-panel-2)', color: 'var(--mm-text)', borderRadius: 6, whiteSpace: 'pre-wrap' }}>{`cd backend
node scripts/recover.js status
node scripts/recover.js reset-owner <usuario>
node scripts/recover.js lockdown on | off`}</pre>
            </div>
          </Card>
        </Col>
      </Row>

      {arming && <EmergencyModal onClose={() => { setArming(false); load(); }} />}

      <Modal show={askPassword} onHide={() => setAskPassword(false)} centered>
        <Form onSubmit={generate}>
          <Modal.Header closeButton><Modal.Title>Generar códigos de recuperación</Modal.Title></Modal.Header>
          <Modal.Body>
            <p className="small text-muted">Se crea un juego nuevo de 8 códigos y <strong>los anteriores dejan de funcionar</strong>. Confirma con tu contraseña.</p>
            <Form.Control type="password" autoFocus autoComplete="current-password" placeholder="Tu contraseña" value={password} onChange={e => setPassword(e.target.value)} />
            {error && <div className="alert alert-danger mt-3 mb-0">{error}</div>}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setAskPassword(false)}>Cancelar</Button>
            <Button type="submit" disabled={busy || !password}>{busy ? 'Generando...' : 'Generar'}</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {codes && <CodesModal codes={codes} onClose={() => setCodes(null)} />}
    </>
  );
};

export default AdminEmergencyPanel;
